<template>
  <v-container fluid :class="currentSubEvent == null ? 'fill-height' : ''">
    <v-row v-if="currentSubEvent == null">
      <v-col>
        <Loading />
      </v-col>
    </v-row>

    <v-row align="center" v-else>
      <v-col cols="12">
        <v-breadcrumbs :items="breadCrumbItems" large>
          <template v-slot:item="{ item }">
            <v-breadcrumbs-item
              :disabled="item.disabled"
              :class="[
                'text-capitalize px-0',
                {
                  'cursor-pointer':
                    item.routeName != $route.name && !item.disabled,
                },
              ]"
              @click="handleRoute(item)"
            >
              <div
                :class="[
                  item.routeName != $route.name && !item.disabled
                    ? 'primary--text'
                    : 'grey--text',
                  'text-subtitle ',
                ]"
              >
                {{ item.text }}
              </div>
            </v-breadcrumbs-item>
          </template>
        </v-breadcrumbs>
      </v-col>
      <v-col cols="12">
        <v-card class="pl-12 py-5 pr-10" outlined>
          <v-card-title v-if="isEditMode">{{
            currentSubEvent.name
          }}</v-card-title>
          <v-card-title v-else>Add a sub-event </v-card-title>
          <v-card-text>
            <!---Start:Stepper while creating the sub-event--->
            <v-stepper
              v-model="currentStep"
              alt-labels
              class="elevation-0"
              v-if="!isEditMode"
            >
              <!--Start: Stepper Header--->
              <v-stepper-header class="elevation-0">
                <v-stepper-step
                  :complete="1 < currentStep"
                  key="`subEventStep1`"
                  :class="['text-no-wrap']"
                  :step="1"
                  color="primary lighten-1"
                  error-icon="mdi-alert-circle"
                >
                  <span>Sub-event details</span>
                </v-stepper-step>
                <v-divider :key="`subEventStep1Divider`"></v-divider>
                <v-stepper-step
                  :complete="2 < currentStep"
                  key="`subEventStep2`"
                  :class="['text-no-wrap']"
                  :step="2"
                  color="primary lighten-1"
                  error-icon="mdi-alert-circle"
                >
                  <span>Featured Profiles</span>
                </v-stepper-step>
                <v-divider :key="`subEventStep2Divider`"></v-divider>
                <v-stepper-step
                  :complete="3 < currentStep"
                  key="`subEventStep3`"
                  :class="['text-no-wrap']"
                  :step="3"
                  color="primary lighten-1"
                  error-icon="mdi-alert-circle"
                >
                  <span>Agenda</span>
                </v-stepper-step>
              </v-stepper-header>
              <!--End: Stepper Header--->

              <v-stepper-items>
                <v-stepper-content step="1">
                  <AddEditSubEventDetails
                    ref="addEditSubEventDetailsComponentRef"
                  />
                </v-stepper-content>
                <v-stepper-content step="2">
                  <AddEditSponsors :event-category="`subEvent`"/>
                </v-stepper-content>
                <v-stepper-content step="3">
                  <AddEditSubEventAgenda />
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
            <!---End:Stepper while creating the sub-event--->

            <!---Start:Tabs while editing the sub-event--->
            <template v-else>
              <v-tabs
                v-model="currentSubEvent.currentTab"
                active-class="primary--text"
              >
                <v-tab active-class="primary--text" class="text-capitalize"
                  >Sub-event details</v-tab
                >
                <v-tab active-class="primary--text" class="text-capitalize"
                  >Sessions</v-tab
                >
                <v-tab active-class="primary--text" class="text-capitalize"
                  >Featured Profiles</v-tab
                >
                <v-tab active-class="primary--text" class="text-capitalize"
                  >Registrants</v-tab
                >
              </v-tabs>
              <v-divider></v-divider>
              <v-tabs-items v-model="currentSubEvent.currentTab">
                <v-tab-item>
                  <AddEditSubEventDetails
                    ref="addEditSubEventDetailsComponentRef"
                /></v-tab-item>
                <v-tab-item> <AddEditSubEventAgenda /></v-tab-item>
                <v-tab-item> <AddEditSponsors :event-category="`subEvent`"/></v-tab-item>
                <v-tab-item><SubEventRegistrants /></v-tab-item>
              </v-tabs-items>
            </template>
            <!---End:Tabs while editing the sub-event--->
          </v-card-text>
          <v-card-actions>
            <v-row>
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
              <v-col cols="12" class="d-flex justify-start">
                <v-btn
                  width="150"
                  color="grey lighten-4"
                  depressed
                  class="text-body-1 mr-4"
                  large
                  v-if="!isEditMode"
                  @click="handleBack"
                  >{{ backButtonText }}</v-btn
                >

                <v-btn
                  v-if="showSaveBtn"
                  width="150"
                  color="primary"
                  depressed
                  class="text-body-1"
                  @click="handleNext"
                  large
                  >{{ saveBtnText }}</v-btn
                >
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Loading from "@/components/Loading";
import { mapGetters, mapActions } from "vuex";
import AddEditSubEventDetails from "@/components/MultiConnect/SubEvent/AddEditSubEventDetails";
import AddEditSponsors from "@/components/MultiConnect/AddEditSponsors";
import AddEditSubEventAgenda from "@/components/MultiConnect/SubEvent/SessionsTable";
import SubEventRegistrants from "@/components/MultiConnect/SubEvent/SubEventRegistrants";
import constants from "@/shared/constants";
import Event from "@/models/Event";
import {onSnapshot} from "firebase/firestore";
import {eventDocRef} from "@/shared/firebase_utils";

export default {
  components: {
    Loading,
    AddEditSubEventDetails,
    AddEditSponsors,
    AddEditSubEventAgenda,
    SubEventRegistrants,
  },
  computed: {
    ...mapGetters([
      "currentSubEvent",
      "userProfile",
      "selectedBusinessProfileId",
      "businessProfile",
      "currentEvent",
    ]),

    currentStep() {
      let currentStepItem = this.stepper.find((step) => {
        return step.stepName == this.currentSubEvent.currentStep;
      });
      if (currentStepItem) return currentStepItem.step;
      else return 1;
    },
    isEditMode() {
      return !this.currentSubEvent.isCreationInProgress;
    },
    saveBtnText() {
      if (this.isEditMode) return "Save";
      else {
        if (this.currentSubEvent.currentStep == constants.EVENT_AGENDA)
          return "Done";
        else return "Next";
      }
    },
    showSaveBtn() {
      if (this.isEditMode && this.currentSubEvent.currentTab != 0) return false;
      else return true;
    },
    breadCrumbItems() {
      if (this.currentEvent && this.currentSubEvent) {
        let currentRouteName = this.isEditMode
          ? this.currentSubEvent.name
          : "Add a sub-event";
        let items = [
          {
            text: "EventConnect",
            disabled: false,
            routeName: "MultiConnect",
            params: "",
          },
          {
            text: this.currentEvent.name,
            disabled: false,
            routeName: "editMultiConnect",
            params: { eventId: this.currentEvent.id },
            query:{tab:2}
          },
          {
            text: currentRouteName,
            disabled: true,
            routeName: "addEditSubEvent",
            params: {
              eventId: this.currentEvent.id,
              subEventId: this.currentSubEvent.id,
            },
          },
        ];
        return items;
      } else return [];
    },
    backButtonText() {
      if (this.currentStep == 1) return "Cancel";
      else return "Back";
    },
  },
  data() {
    return {
      stepper: [
        {
          step: 1,
          stepName: "EVENT_DETAILS",
        },
        {
          step: 2,
          stepName: "EVENT_SPONSOR",
        },
        {
          step: 3,
          stepName: "EVENT_AGENDA",
        },
      ],
      unsubscribeEventsSnapshot: null,
      unsubscribeSubEventsSnapshot: null,
    };
  },
  created() {
    this.eventId = this.$route.params.eventId;
    this.subEventId = this.$route.params.subEventId;
    let tab = this.$route.query.tab;

    console.log("created called");

    //Get event doc from firestore

    this.unsubscribeEventsSnapshot = onSnapshot(
          eventDocRef(this.eventId),
          async (documentSnapshot) => {
        //Event exists in firestore
        if (documentSnapshot.data() != undefined) {
          let newEvent = Event.fromFireStore(documentSnapshot);

          // Update the current tab
          if (this.currentEvent != null) {
            newEvent.currentTab = this.currentEvent.currentTab;
          }

          await this.setCurrentEvent(newEvent);
        } else {
          console.log("event document does not exist in Fs");
        }
      });

    //Get sub-event doc from Firestore
    this.unsubscribeSubEventsSnapshot = onSnapshot(
          eventDocRef(this.subEventId),
          async (documentSnapshot) => {
        //Sub-Event exists in firestore
        if (documentSnapshot.data() != undefined) {
          console.log("event is defined");
          let newSubEvent = Event.fromFireStore(documentSnapshot);

          //Set tab query param
          if(tab)
            newSubEvent.currentTab = tab;

          // Update the current tab
          if (this.currentSubEvent != null) {
            // newSubEvent.currentTab = this.currentSubEvent.currentTab;
            newSubEvent.currentTab = 0;
          }
          
          await this.setCurrentSubEvent(newSubEvent);

          if(tab)
            this.currentSubEvent.currentTab = tab;

          
        } else {
          console.log("sub-event document does not exist in Fs");

          let subEvent = new Event({
            id: this.subEventId,
            businessProfileId: this.selectedBusinessProfileId,
            hostName: this.businessProfile.businessName,
            isCreationInProgress: true,
            parentMultiConnectEventId: this.currentEvent.id,
            currentStep: "EVENT_DETAILS",
            linkPrivacy: this.currentEvent.linkPrivacy,
            eventCategory: constants.EVENT_CATEGORY.subEvent,
          });

          this.setCurrentSubEvent(subEvent);

          //Set the timezone
          this.currentSubEvent.timezone = this.currentEvent.timezone;
        }
      });
  },
  destroyed() {
    this.unsubscribeEventsSnapshot();
    this.clearCurrentEvent();
    this.unsubscribeSubEventsSnapshot();
    this.clearCurrentSubEvent();
  },
  methods: {
    ...mapActions([
      "setCurrentEvent",
      "initializeEvent",
      "clearCurrentEvent",
      "setCurrentSubEvent",
      "clearCurrentSubEvent",
      "showSnackbar",
    ]),
    handleNext() {
      //Start: Form validation
      let isValid;
      if (this.currentStep == 1)
        isValid = this.$refs.addEditSubEventDetailsComponentRef.validatePage();
      else isValid = true;
      //End: Form validation
      if (this.currentEvent.eventCategory === 'travel') {
        const startDate = this.currentSubEvent.startDateTime;
        const endDate = this.currentSubEvent.endDateTime;
        if (startDate.getTime() > endDate.getTime() && this.currentSubEvent.enableEndTime) {
          this.showSnackbar({ show: true, text: "End time should be after start time" });
          return;
        }
      }

      if (isValid) {
        let isLastStepOfWizard = false;
        if (!this.isEditMode) {
          //Get the next step for the wizard
          if (
            this.currentSubEvent.currentStep == constants.EVENT_DETAILS_PAGE
          ) {
            this.currentSubEvent.currentStep = constants.EVENT_SPONSOR;
          } else if (
            this.currentSubEvent.currentStep == constants.EVENT_SPONSOR
          ) {
            this.currentSubEvent.currentStep = constants.EVENT_AGENDA;
          } else {
            this.currentSubEvent.isCreationInProgress = false;
            isLastStepOfWizard = true;
          }

          //Check if sub-event's id has been added to the main event already
          let subEventIndex = this.currentEvent.linkedSubEvents.findIndex(
            (subEventId) => subEventId == this.currentSubEvent.id
          );

          //Add the sub-event id to current event
          if (subEventIndex == -1)
            this.currentEvent.linkedSubEvents.push(this.currentSubEvent.id);
        }

        console.log("this.currentSubEvent: ", this.currentSubEvent);

        //Save the event and sub-event
        this.currentSubEvent.save();
        this.currentEvent.save();

        //Only show the snackbar if user is on last step of stepper and done with creation or while they are editing the sub-event
        if (isLastStepOfWizard || this.isEditMode)
          this.showSnackbar({ show: true, text: "Saved successfully!" });

        if (isLastStepOfWizard) {
          //Navigate to event program table
          this.$router.push({
            name: "editMultiConnect",
            params: { eventId: this.currentEvent.id },
            query: { tab: 2 },
          });
        }
      } else {
        console.log("Form is invalid, please try again");
      }
    },
    handleRoute(item) {
      if (!item.disabled) {
        let route = { name: item.routeName, params: item.params };
        if(item.query){
          route.query = item.query;
        }
        this.$router.push(route);
      }
    },
    handleBack() {
      if (this.currentStep == 1)
        this.$router.push({
          name: "editMultiConnect",
          params: { eventId: this.currentEvent.id },
        });
      else {
        if (this.currentStep == 2)
          this.currentSubEvent.currentStep = constants.EVENT_DETAILS_PAGE;
        else this.currentSubEvent.currentStep = constants.EVENT_SPONSOR;

        this.currentSubEvent.save();
      }
    },
  },
};
</script>

<style>
</style>